import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useSolutionOrg } from '../config/useSolutionOrg';
import { selectMemberSession } from '../store/slices/onboardingSlice';
import {
  ResponseError, enrollMember, getMemberInformation,
} from '../store/thunks/onboardingThunk';
import { ONBOARDING_BASE_ROUTE } from '../routes/namedRoutes';
import { useCustomEventTracking } from './useCustomEventTracking';
import { EverProEdgePublicSolutionOrgName, ServiceFusionSolutionOrgName, ServiceNationSolutionOrgName } from './constants';
import { useOnboardingFlowTest } from './hooks/useOnboardingFlowTest';
import { EnrollmentRequest, toEnrolledMember } from './models/EnrollmentRequest';
import { isUserInitiatedHomeDepotEnrollment } from './homeDepotEnrollmentIntent';
import { getDashboardEntryRoute } from './dashboardEntryRoute';

interface useCompanyEnrollmentInfoProps {
  calledFromOnboardingComponent: boolean;
  redirectPath?: string;
  enableOnboardingFlowTestRedirect?: boolean;
}

export function useCompanyEnrollmentInfo({
  calledFromOnboardingComponent,
  enableOnboardingFlowTestRedirect = false,
  redirectPath,
}: useCompanyEnrollmentInfoProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { trackPartnerEnrollment, trackRequest } = useCustomEventTracking();
  const location = useLocation();
  const { solutionOrgName, envSolutionOrgId } = useSolutionOrg();
  const memberSession = useAppSelector(selectMemberSession);
  const { isTestReady, shouldSkipOnboarding } = useOnboardingFlowTest({
    enabled: enableOnboardingFlowTestRedirect,
  });
  const shouldRedirectForOnboardingFlowTest =
    enableOnboardingFlowTestRedirect && shouldSkipOnboarding;
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const dashboardEntryRoute = getDashboardEntryRoute(solutionOrgName);
  const currentDashboardPath = location.pathname === '/' ? dashboardEntryRoute : currentPath;
  const fallbackRedirectPath = redirectPath || currentDashboardPath || dashboardEntryRoute;
  const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
    state: location.state,
    search: location.search,
    redirectPath,
  });

  useEffect(() => {
    let isActive = true;
    const navigateIfActive = (path: string) => {
      if (!isActive) {
        return;
      }

      const currentPathWithSearch = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (path !== currentPathWithSearch) {
        navigate(path);
      }
    };

    if (enableOnboardingFlowTestRedirect && !isTestReady) {
      return () => {
        isActive = false;
      };
    }

    // Skip for Service Nation, Service Fusion, and EverPro Edge Public
    if (solutionOrgName === ServiceNationSolutionOrgName ||
      solutionOrgName === EverProEdgePublicSolutionOrgName ||
      solutionOrgName === ServiceFusionSolutionOrgName) {
      return () => {
        isActive = false;
      };
    }
    dispatch(
      getMemberInformation({ trackRequest }),
    )
      .then(unwrapResult)
      .then((getMemberInformationResponse) => {
        // Check if member already enrolled in edge. Home Depot setup is no longer forced here:
        // new members reach it at the end of Edge onboarding, everyone else enrolls from the
        // partners page.
        if (getMemberInformationResponse?.edgeEnrollmentDate) {
          if (shouldRedirectForOnboardingFlowTest && !userInitiatedHdEnrollment) {
            navigateIfActive(fallbackRedirectPath);
          } else if (calledFromOnboardingComponent) {
            navigateIfActive(fallbackRedirectPath);
          }
          // Anywhere else (e.g. the HD setup pages) don't navigate - let the form handle
          // completion navigation. This preserves location.state through enrollment.
        } else if (shouldRedirectForOnboardingFlowTest) {
          // user's company has not yet enrolled in reward: Optimizely test group auto-enrolls
          const solutionOrgId = memberSession.solutionOrgId ||
            (envSolutionOrgId as string) || '';
          const request: EnrollmentRequest = {
            memberId: getMemberInformationResponse.id,
            member: toEnrolledMember(getMemberInformationResponse),
            forms: [],
            partners: [],
          };
          dispatch(enrollMember({ solutionOrgId, request, trackRequest }))
            .then(unwrapResult)
            .then(() => {
              trackPartnerEnrollment('Submit', 'Form', {
                enrollmentType: 'New',
                isOptimizelyEvent: true,
              });
              if (!userInitiatedHdEnrollment) {
                navigateIfActive(fallbackRedirectPath);
              }
            })
            .catch((error: ResponseError) => {
              trackRequest('Error', 'Post', 'EnrollMember', {
                error,
                apiResponseErrorStatus: error.apiResponseErrorStatus,
              });
              if (!calledFromOnboardingComponent) {
                navigateIfActive(ONBOARDING_BASE_ROUTE);
              }
            });
        } else if (!calledFromOnboardingComponent) {
          navigateIfActive(redirectPath || ONBOARDING_BASE_ROUTE);
        }
      })
      .catch((error: ResponseError) => {
        trackRequest('Error', 'Get', 'CompanyEnrollmentInformation', {
          error,
          apiResponseErrorStatus: error.apiResponseErrorStatus,
        });
      });

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableOnboardingFlowTestRedirect,
    isTestReady,
    solutionOrgName,
    shouldRedirectForOnboardingFlowTest,
    fallbackRedirectPath,
    memberSession.solutionOrgId,
    envSolutionOrgId,
    trackPartnerEnrollment,
    trackRequest,
  ]);

  return null;
}
